import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Chip,
    Alert,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShare, faTimes } from '@fortawesome/free-solid-svg-icons';
import { UserRole } from '../types';

interface UserToAdd {
    emailId: string;
    role: UserRole;
}

interface ShareModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (users: UserToAdd[]) => Promise<void>;
    templateName: string;
}

export const ShareModal: React.FC<ShareModalProps> = ({
    open,
    onClose,
    onSubmit,
    templateName,
}) => {
    const [emailInput, setEmailInput] = useState('');
    const [selectedRole, setSelectedRole] = useState<UserRole>('viewer');
    const [usersToAdd, setUsersToAdd] = useState<UserToAdd[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleAddUser = () => {
        const email = emailInput.trim();
        if (email && !usersToAdd.some(user => user.emailId === email)) {
            setUsersToAdd([...usersToAdd, { emailId: email, role: selectedRole }]);
            setEmailInput('');
            setSelectedRole('viewer');
            setError('');
        }
    };

    const handleRemoveUser = (emailToRemove: string) => {
        setUsersToAdd(usersToAdd.filter(user => user.emailId !== emailToRemove));
    };

    const handleUpdateRole = (emailId: string, newRole: UserRole) => {
        setUsersToAdd(usersToAdd.map(user => 
            user.emailId === emailId ? { ...user, role: newRole } : user
        ));
    };

    const handleSubmit = async () => {
        if (usersToAdd.length === 0) {
            setError('Please add at least one user');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await onSubmit(usersToAdd);
            setUsersToAdd([]);
            setEmailInput('');
            setSelectedRole('viewer');
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to share template');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddUser();
        }
    };

    const handleClose = () => {
        setUsersToAdd([]);
        setEmailInput('');
        setSelectedRole('viewer');
        setError('');
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FontAwesomeIcon icon={faShare} />
                    Share Template
                </Box>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Share "{templateName}" with other users by entering their email addresses and selecting their roles below.
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                        <TextField
                            fullWidth
                            label="Email Address"
                            type="email"
                            value={emailInput}
                            onChange={(e) => setEmailInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Enter email address"
                            disabled={loading}
                            sx={{ flex: 2 }}
                        />
                        <FormControl sx={{ minWidth: 120, flex: 1 }}>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={selectedRole}
                                label="Role"
                                onChange={(e) => setSelectedRole(e.target.value as UserRole)}
                                disabled={loading}
                            >
                                <MenuItem value="viewer">Viewer</MenuItem>
                                <MenuItem value="manager">Manager</MenuItem>
                            </Select>
                        </FormControl>
                        <Button
                            onClick={handleAddUser}
                            disabled={!emailInput.trim() || loading}
                            variant="contained"
                            size="small"
                            sx={{ minWidth: 'auto', px: 2, height: 56 }}
                        >
                            Add
                        </Button>
                    </Box>
                </Box>

                {usersToAdd.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Users to share with ({usersToAdd.length}):
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {usersToAdd.map((user) => (
                                <Box
                                    key={user.emailId}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        bgcolor: 'background.paper',
                                    }}
                                >
                                    <Chip
                                        label={user.emailId}
                                        onDelete={() => handleRemoveUser(user.emailId)}
                                        deleteIcon={<FontAwesomeIcon icon={faTimes} />}
                                        sx={{
                                            flex: 1,
                                            '& .MuiChip-deleteIcon': {
                                                fontSize: '0.875rem',
                                            },
                                        }}
                                    />
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                        <Select
                                            value={user.role}
                                            onChange={(e) => handleUpdateRole(user.emailId, e.target.value as UserRole)}
                                            disabled={loading}
                                            sx={{ height: 32 }}
                                        >
                                            <MenuItem value="viewer">Viewer</MenuItem>
                                            <MenuItem value="manager">Manager</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={usersToAdd.length === 0 || loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <FontAwesomeIcon icon={faShare} />}
                >
                    {loading ? 'Sharing...' : 'Share Template'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}; 